import { SITE_CONFIG } from '../config';

const ANALYTICS_BASE_URL = 'https://tophamtechbe.com/api/treas/auth/analytics/identity';
const MEASUREMENT_ID = SITE_CONFIG.ANALYTICS.GA4_MEASUREMENT_ID;
const SITE_KEY = String(SITE_CONFIG.SITE_ID).toLowerCase();
const STORAGE_PREFIX = `treas:analytics:${SITE_KEY}`;
const PREFERENCE_KEY = `${STORAGE_PREFIX}:preference`;
const NOTICE_KEY = `${STORAGE_PREFIX}:notice-seen`;
const VISITOR_KEY = `${STORAGE_PREFIX}:visitor-id`;
const LEAD_SUBJECT_KEY = `${STORAGE_PREFIX}:lead-subject-id`;
const PENDING_DELETIONS_KEY = `${STORAGE_PREFIX}:pending-deletions`;
const TOUCH_PREFIX = `${STORAGE_PREFIX}:last-touch:`;
const OPTED_OUT = 'opted_out';
const ENABLED = 'enabled';
const TOUCH_INTERVAL_MS = 24 * 60 * 60 * 1000;
const PREFERENCE_EVENT = 'treas-analytics-preference';
const activeTouchRequests = new Map();
const SENSITIVE_QUERY_PARAMETERS = new Set([
  'token', 'code', 'email', 'name', 'username', 'password', 'phone', 'phonenumber'
]);

const safeStorage = {
  get(key) {
    try { return window.localStorage.getItem(key); } catch (error) { return null; }
  },
  set(key, value) {
    try { window.localStorage.setItem(key, value); } catch (error) { /* Best effort only. */ }
  },
  remove(key) {
    try { window.localStorage.removeItem(key); } catch (error) { /* Best effort only. */ }
  }
};

const hasGlobalPrivacyControl = () => (
  typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true
);

export const getAnalyticsPrivacyState = () => {
  const gpc = hasGlobalPrivacyControl();
  const preference = safeStorage.get(PREFERENCE_KEY);
  return {
    enabled: !gpc && preference !== OPTED_OUT,
    gpc,
    optedOut: preference === OPTED_OUT,
    preference
  };
};

export const hasSeenAnalyticsNotice = () => safeStorage.get(NOTICE_KEY) === 'true';
export const markAnalyticsNoticeSeen = () => safeStorage.set(NOTICE_KEY, 'true');

const dispatchPreferenceChange = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new window.CustomEvent(PREFERENCE_EVENT));
  }
};

const createUuid = () => {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;
  if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof cryptoApi?.getRandomValues === 'function') cryptoApi.getRandomValues(bytes);
  else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
};

export const getOrCreateVisitorId = () => {
  if (!getAnalyticsPrivacyState().enabled) return null;
  const existing = safeStorage.get(VISITOR_KEY);
  if (existing) return existing;
  const visitorId = createUuid();
  safeStorage.set(VISITOR_KEY, visitorId);
  return visitorId;
};

const ensureGtagQueue = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
};

const looksLikeStreetAddress = value => /\d/.test(value || '') && /[a-z]/i.test(value || '');

const sanitizeAnalyticsUrl = (value) => {
  if (!value) return undefined;
  try {
    const url = new URL(value, window.location.origin);
    Array.from(url.searchParams.keys()).forEach((key) => {
      const normalizedKey = key.toLowerCase();
      const parameterValue = url.searchParams.get(key);
      if (
        SENSITIVE_QUERY_PARAMETERS.has(normalizedKey) ||
        normalizedKey === 'address' ||
        ((normalizedKey === 'location' || normalizedKey === 'q') && looksLikeStreetAddress(parameterValue))
      ) url.searchParams.delete(key);
    });
    return url.toString();
  } catch (error) {
    return undefined;
  }
};

export const initializeAnalytics = () => {
  if (typeof window === 'undefined' || !MEASUREMENT_ID || !getAnalyticsPrivacyState().enabled) {
    if (typeof window !== 'undefined' && MEASUREMENT_ID) window[`ga-disable-${MEASUREMENT_ID}`] = true;
    return false;
  }
  window[`ga-disable-${MEASUREMENT_ID}`] = false;
  getOrCreateVisitorId();
  ensureGtagQueue();

  if (!document.querySelector(`script[data-treas-ga4="${MEASUREMENT_ID}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
    script.dataset.treasGa4 = MEASUREMENT_ID;
    document.head.appendChild(script);
  }

  if (!window.__treasGa4Configured) {
    window.gtag('js', new Date());
    window.gtag('config', MEASUREMENT_ID, {
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      page_location: sanitizeAnalyticsUrl(window.location.href),
      page_referrer: sanitizeAnalyticsUrl(document.referrer)
    });
    window.__treasGa4Configured = true;
  }
  return true;
};

const getGaClientId = () => new Promise((resolve) => {
  if (!getAnalyticsPrivacyState().enabled || typeof window.gtag !== 'function') return resolve(null);
  let finished = false;
  const finish = (value) => {
    if (finished) return;
    finished = true;
    resolve(typeof value === 'string' && value.length <= 255 ? value : null);
  };
  const timeoutId = window.setTimeout(() => finish(null), 800);
  window.gtag('get', MEASUREMENT_ID, 'client_id', (clientId) => {
    window.clearTimeout(timeoutId);
    finish(clientId);
  });
});

export const getAnalyticsIdentityForAuth = async () => {
  if (!getAnalyticsPrivacyState().enabled) return null;
  try {
    initializeAnalytics();
    const visitorId = getOrCreateVisitorId();
    if (!visitorId) return null;
    const gaClientId = await getGaClientId();
    return { visitorId, ...(gaClientId ? { gaClientId } : {}), analyticsConsent: true };
  } catch (error) {
    return null;
  }
};

const configureAnalyticsUser = (analyticsUserId) => {
  if (typeof window.gtag !== 'function') return;
  window.gtag('config', MEASUREMENT_ID, { user_id: analyticsUserId || null, send_page_view: false });
};

export const applyLeadAnalyticsUser = (analyticsUserId) => {
  if (!getAnalyticsPrivacyState().enabled || typeof analyticsUserId !== 'string' || !analyticsUserId.trim()) return false;
  initializeAnalytics();
  safeStorage.set(LEAD_SUBJECT_KEY, analyticsUserId.trim());
  configureAnalyticsUser(analyticsUserId.trim());
  return true;
};

export const clearAnalyticsUser = () => {
  if (typeof window !== 'undefined') configureAnalyticsUser(null);
};

const getAuthorizationHeaders = user => user?.token ? {
  Authorization: `${user.type || 'Bearer'} ${user.token}`,
  'Content-Type': 'application/json'
} : null;

const getTouchKey = (user, visitorId) => `${TOUCH_PREFIX}${user?.analyticsUserId || user?.id || 'unknown'}:${visitorId}`;

export const touchAnalyticsIdentity = async (user, { force = false } = {}) => {
  if (!getAnalyticsPrivacyState().enabled) return false;
  const headers = getAuthorizationHeaders(user);
  const visitorId = getOrCreateVisitorId();
  if (!headers || !visitorId) return false;
  const touchKey = getTouchKey(user, visitorId);
  const lastTouch = Number(safeStorage.get(touchKey));
  if (!force && Number.isFinite(lastTouch) && Date.now() - lastTouch < TOUCH_INTERVAL_MS) return true;
  if (activeTouchRequests.has(touchKey)) return activeTouchRequests.get(touchKey);

  const request = (async () => {
    try {
      const gaClientId = await getGaClientId();
      const response = await fetch(`${ANALYTICS_BASE_URL}/touch`, {
        method: 'POST', headers,
        body: JSON.stringify({ visitorId, ...(gaClientId ? { gaClientId } : {}), analyticsConsent: true })
      });
      if (!response.ok) return false;
      safeStorage.set(touchKey, String(Date.now()));
      return true;
    } catch (error) {
      return false;
    } finally {
      activeTouchRequests.delete(touchKey);
    }
  })();
  activeTouchRequests.set(touchKey, request);
  return request;
};

const readPendingDeletions = () => {
  try {
    const parsed = JSON.parse(safeStorage.get(PENDING_DELETIONS_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) { return []; }
};

const writePendingDeletions = pending => pending.length
  ? safeStorage.set(PENDING_DELETIONS_KEY, JSON.stringify(pending))
  : safeStorage.remove(PENDING_DELETIONS_KEY);

const queuePendingDeletion = (user, visitorId) => {
  if (!visitorId) return;
  const pending = readPendingDeletions();
  const userKey = user?.token ? String(user.analyticsUserId || user.id || '') : '';
  if (!pending.some(item => item.visitorId === visitorId && item.userKey === userKey)) {
    pending.push({ visitorId, userKey, queuedAt: new Date().toISOString() });
    writePendingDeletions(pending);
  }
};

const deleteAnonymousIdentityMapping = async (visitorId) => {
  if (!visitorId) return false;
  try {
    const response = await fetch(`${ANALYTICS_BASE_URL.replace(/\/identity$/, '')}/lead-identity`, {
      method: 'DELETE', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId: SITE_CONFIG.SITE_ID, visitorId })
    });
    return response.status === 204;
  } catch (error) { return false; }
};

const deleteIdentityMapping = async (user, visitorId) => {
  const headers = getAuthorizationHeaders(user);
  if (!headers || !visitorId) return false;
  try {
    const response = await fetch(ANALYTICS_BASE_URL, {
      method: 'DELETE', headers, body: JSON.stringify({ visitorId })
    });
    return response.status === 204;
  } catch (error) { return false; }
};

export const retryPendingAnalyticsDeletions = async (user) => {
  const userKey = String(user?.analyticsUserId || user?.id || '');
  const pending = readPendingDeletions();
  if (!pending.length) return true;
  const remaining = [];
  for (const item of pending) {
    if (item.userKey && (!user?.token || item.userKey !== userKey)) {
      remaining.push(item);
      continue;
    }
    // eslint-disable-next-line no-await-in-loop
    const deleted = item.userKey
      ? await deleteIdentityMapping(user, item.visitorId)
      : await deleteAnonymousIdentityMapping(item.visitorId);
    if (!deleted) remaining.push(item);
  }
  writePendingDeletions(remaining);
  return remaining.length === 0;
};

const clearGaCookies = () => {
  if (typeof document === 'undefined') return;
  const names = document.cookie.split(';').map(cookie => cookie.trim().split('=')[0])
    .filter(name => name === '_ga' || name.startsWith('_ga_'));
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const domains = ['', hostname, `.${hostname}`];
  if (parts.length > 2) domains.push(`.${parts.slice(-2).join('.')}`);
  names.forEach(name => domains.forEach(domain => {
    const domainPart = domain ? `; domain=${domain}` : '';
    document.cookie = `${name}=; Max-Age=0; path=/${domainPart}; SameSite=Lax`;
  }));
};

const removeTouchMarkers = () => {
  try {
    Object.keys(window.localStorage).filter(key => key.startsWith(TOUCH_PREFIX))
      .forEach(key => window.localStorage.removeItem(key));
  } catch (error) { /* Best effort only. */ }
};

export const disableAnalytics = async (user) => {
  const visitorId = safeStorage.get(VISITOR_KEY);
  clearAnalyticsUser();
  safeStorage.set(PREFERENCE_KEY, OPTED_OUT);
  markAnalyticsNoticeSeen();
  window[`ga-disable-${MEASUREMENT_ID}`] = true;
  if (visitorId) queuePendingDeletion(user, visitorId);
  safeStorage.remove(VISITOR_KEY);
  safeStorage.remove(LEAD_SUBJECT_KEY);
  removeTouchMarkers();
  clearGaCookies();
  dispatchPreferenceChange();
  await retryPendingAnalyticsDeletions(user);
};

export const enableAnalytics = async (user) => {
  if (hasGlobalPrivacyControl()) return false;
  safeStorage.set(PREFERENCE_KEY, ENABLED);
  markAnalyticsNoticeSeen();
  window[`ga-disable-${MEASUREMENT_ID}`] = false;
  initializeAnalytics();
  if (user?.analyticsUserId) configureAnalyticsUser(user.analyticsUserId);
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_title: document.title,
      page_location: sanitizeAnalyticsUrl(window.location.href),
      page_referrer: sanitizeAnalyticsUrl(document.referrer)
    });
  }
  dispatchPreferenceChange();
  retryPendingAnalyticsDeletions(user);
  if (user?.token) touchAnalyticsIdentity(user, { force: true });
  return true;
};

export const completeAnalyticsAuthentication = (user, eventName) => {
  if (!getAnalyticsPrivacyState().enabled) return;
  initializeAnalytics();
  safeStorage.remove(LEAD_SUBJECT_KEY);
  if (user?.analyticsUserId) configureAnalyticsUser(user.analyticsUserId);
  if (eventName && typeof window.gtag === 'function') window.gtag('event', eventName, { method: 'website' });
  touchAnalyticsIdentity(user);
  retryPendingAnalyticsDeletions(user);
};

export const syncStoredAnalyticsUser = (user) => {
  if (!getAnalyticsPrivacyState().enabled) return retryPendingAnalyticsDeletions(user);
  initializeAnalytics();
  if (user?.analyticsUserId) configureAnalyticsUser(user.analyticsUserId);
  else {
    const leadSubjectId = safeStorage.get(LEAD_SUBJECT_KEY);
    if (leadSubjectId) configureAnalyticsUser(leadSubjectId);
  }
  touchAnalyticsIdentity(user);
  retryPendingAnalyticsDeletions(user);
};

const getSafeSearchTerm = (searchTerm, filters) => {
  const value = String(searchTerm || '').trim();
  if (filters?.listingKey) return 'listing_key';
  if (looksLikeStreetAddress(value)) return 'address_search';
  return value || 'map_area';
};

const getSearchFilterParameters = (filters = {}) => {
  const source = filters instanceof URLSearchParams ? Object.fromEntries(filters.entries()) : filters;
  const mappings = {
    propertyType: 'property_type', propertySubType: 'property_subtype',
    minBedrooms: 'min_bedrooms', maxBedrooms: 'max_bedrooms',
    minBathrooms: 'min_bathrooms', maxBathrooms: 'max_bathrooms',
    minPrice: 'min_price', maxPrice: 'max_price', minSqft: 'min_sqft', maxSqft: 'max_sqft',
    minYearBuilt: 'min_year_built', maxYearBuilt: 'max_year_built', hasPool: 'has_pool',
    hasFence: 'has_fence', hasMedia: 'has_media', schoolDistrict: 'school_district', priceSort: 'price_sort'
  };
  return Object.entries(mappings).reduce((parameters, [sourceKey, eventKey]) => {
    const value = source?.[sourceKey];
    if (value !== undefined && value !== null && value !== '' && value !== false) parameters[eventKey] = value;
    return parameters;
  }, {});
};

export const trackPropertySearch = ({ searchTerm, filters = {}, results = [], resultCount, surface = 'property_search' } = {}) => {
  if (!getAnalyticsPrivacyState().enabled || typeof window === 'undefined') return false;
  if (!initializeAnalytics() || typeof window.gtag !== 'function') return false;
  const normalizedFilters = filters instanceof URLSearchParams ? Object.fromEntries(filters.entries()) : filters;
  const seenListingKeys = new Set();
  const items = results.reduce((listingItems, property) => {
    const listingKey = property?.listing_key;
    if (!listingKey || property?.isPrivate || String(listingKey).startsWith('PRIVATE_')) return listingItems;
    const normalizedKey = String(listingKey);
    if (seenListingKeys.has(normalizedKey) || listingItems.length >= 200) return listingItems;
    seenListingKeys.add(normalizedKey);
    listingItems.push({ item_id: normalizedKey, listing_key: normalizedKey, item_list_name: 'property_search_results', index: listingItems.length });
    return listingItems;
  }, []);
  const exactListingKey = normalizedFilters?.listingKey;
  window.gtag('event', 'view_search_results', {
    search_term: getSafeSearchTerm(searchTerm, normalizedFilters), search_surface: surface,
    result_count: Number.isFinite(Number(resultCount)) ? Number(resultCount) : results.length,
    ...getSearchFilterParameters(normalizedFilters),
    ...(exactListingKey ? { listing_key: String(exactListingKey) } : {}),
    ...(items.length ? { items } : {})
  });
  return true;
};

export const trackPropertyView = ({ property, surface = 'property_modal' } = {}) => {
  if (!getAnalyticsPrivacyState().enabled || typeof window === 'undefined') return false;
  if (!initializeAnalytics() || typeof window.gtag !== 'function') return false;
  const listingKey = property?.listing_key;
  if (!listingKey) return false;
  const normalizedKey = String(listingKey);
  const isPrivate = property?.isPrivate || normalizedKey.startsWith('PRIVATE_');
  const numericPrice = Number(String(property?.current_price ?? property?.price ?? '').replace(/[^0-9.-]/g, ''));
  const propertyType = property?.property_type || property?.property_sub_type;
  const item = { item_id: normalizedKey, listing_key: normalizedKey, item_list_name: 'property_modal' };
  if (propertyType) item.item_category = String(propertyType);
  if (Number.isFinite(numericPrice) && numericPrice > 0) item.price = numericPrice;
  window.gtag('event', 'view_item', {
    listing_key: normalizedKey, view_surface: surface, property_source: isPrivate ? 'private' : 'mls',
    ...(propertyType ? { property_type: String(propertyType) } : {}),
    ...(Number.isFinite(numericPrice) && numericPrice > 0 ? { listing_price: numericPrice } : {}), items: [item]
  });
  return true;
};

export const ANALYTICS_PREFERENCE_EVENT = PREFERENCE_EVENT;
